// Example GitHub + Linear activity, shown until the live sources answer (and when they aren't configured).

import { Stage } from '@/services/systemHealth';

export type Env = Stage;
export const envs: { id: Env; label: string }[] = [
    { id: 'test', label: 'Test' },
    { id: 'uat', label: 'UAT' },
    { id: 'prod', label: 'Prod' },
];

export interface MergedPr {
    repo: string;
    number: number;
    title: string;
    author: string;
    base: string;
    additions: number;
    deletions: number;
    linearId?: string;
    mergedAt: Date;
    /** Set by the API; dummy rows build the URL from repo + number. */
    url?: string;
}

export interface LinearIssue {
    id: string;
    title: string;
    /** Workflow state as named in Linear, e.g. "Ready to test". */
    state: string;
    /** Linear's category: in-flight issues are 'started', done ones 'completed'. */
    stateType: 'started' | 'completed';
    /** Column order on the team's board. */
    statePosition: number;
    assignee: string | null;
    updatedAt: Date;
    url?: string;
}

const minutesAgo = (m: number) => new Date(Date.now() - m * 60_000);
const hoursAgo = (h: number) => minutesAgo(h * 60);

export const mergedPrs: MergedPr[] = [
    {
        repo: 'bygr-admin',
        number: 203,
        title: 'System health dashboard',
        author: 'jonathan',
        base: 'master',
        additions: 1240,
        deletions: 18,
        linearId: 'RUC-601',
        mergedAt: minutesAgo(14),
    },
    {
        repo: 'bygr-backend',
        number: 1882,
        title: 'Change request summary returns empty list instead of null',
        author: 'mira',
        base: 'develop',
        additions: 42,
        deletions: 9,
        linearId: 'RUC-594',
        mergedAt: minutesAgo(48),
    },
    {
        repo: 'bygr-core-api',
        number: 311,
        title: 'Raise SQL command timeout for unit search',
        author: 'sam',
        base: 'main',
        additions: 6,
        deletions: 2,
        linearId: 'RUC-598',
        mergedAt: hoursAgo(2),
    },
    {
        repo: 'bygr-frontend-web',
        number: 977,
        title: 'Guard against missing units in project overview',
        author: 'mira',
        base: 'develop',
        additions: 31,
        deletions: 12,
        linearId: 'RUC-595',
        mergedAt: hoursAgo(3),
    },
    {
        repo: 'rubus-ui',
        number: 148,
        title: 'Remove legacy Tabs styles',
        author: 'alex',
        base: 'main',
        additions: 12,
        deletions: 870,
        mergedAt: hoursAgo(5),
    },
    {
        repo: 'rubus-frontend-sales',
        number: 402,
        title: 'Playwright: cover buyer invitation flow',
        author: 'sam',
        base: 'main',
        additions: 388,
        deletions: 4,
        linearId: 'RUC-588',
        mergedAt: hoursAgo(7),
    },
    {
        repo: 'bygr-backend-am-qm',
        number: 655,
        title: 'Retry core-api calls with backoff',
        author: 'alex',
        base: 'develop',
        additions: 97,
        deletions: 31,
        linearId: 'RUC-599',
        mergedAt: hoursAgo(9),
    },
    {
        repo: 'bygr-database',
        number: 129,
        title: 'Index ProjectMembers(ApplicationUserId, Source)',
        author: 'mira',
        base: 'master',
        additions: 24,
        deletions: 0,
        linearId: 'RUC-527',
        mergedAt: hoursAgo(20),
    },
    {
        repo: 'ruben-backend',
        number: 88,
        title: 'Pinecone query timeout from config',
        author: 'jonathan',
        base: 'main',
        additions: 19,
        deletions: 7,
        mergedAt: hoursAgo(26),
    },
    {
        repo: 'bygr-backend',
        number: 1879,
        title: 'Delete unused PDF export v1',
        author: 'alex',
        base: 'develop',
        additions: 3,
        deletions: 1415,
        linearId: 'RUC-571',
        mergedAt: hoursAgo(30),
    },
    {
        repo: 'bygr-frontend-web',
        number: 975,
        title: 'Survey v2 question bank in CM',
        author: 'jonathan',
        base: 'develop',
        additions: 2105,
        deletions: 340,
        linearId: 'RUC-560',
        mergedAt: hoursAgo(44),
    },
    {
        repo: 'bygr-backend',
        number: 1876,
        title: 'AI categorisation of documents against building part table',
        author: 'sam',
        base: 'develop',
        additions: 1630,
        deletions: 120,
        linearId: 'RUC-582',
        mergedAt: hoursAgo(70),
    },
];

export type PrState = 'draft' | 'review' | 'changes' | 'approved';
export type ChecksState = 'passing' | 'failing' | 'pending';

export interface OpenPr {
    repo: string;
    number: number;
    title: string;
    author: string;
    state: PrState;
    /** Null when the PR has no checks at all. */
    checks: ChecksState | null;
    url?: string;
    additions: number;
    deletions: number;
    linearId?: string;
    /** Asked to review and not done so yet. */
    reviewers?: string[];
    openedAt: Date;
}

export const openPrs: OpenPr[] = [
    {
        repo: 'bygr-admin',
        number: 204,
        title: 'System health: GitHub activity tab',
        author: 'jonathan',
        state: 'draft',
        checks: 'pending',
        additions: 640,
        deletions: 40,
        linearId: 'RUC-602',
        openedAt: minutesAgo(25),
    },
    {
        repo: 'bygr-backend',
        number: 1885,
        title: 'Change orders: bulk approve from buyer portal',
        author: 'sam',
        state: 'review',
        checks: 'passing',
        additions: 412,
        deletions: 58,
        linearId: 'RUC-590',
        openedAt: hoursAgo(52),
    },
    {
        repo: 'rubus-backend-communicore',
        number: 214,
        title: 'Handle SMS bounce webhooks',
        author: 'alex',
        state: 'review',
        checks: 'failing',
        additions: 233,
        deletions: 17,
        linearId: 'RUC-597',
        openedAt: hoursAgo(6),
    },
    {
        repo: 'rubus-frontend-flatfinder',
        number: 167,
        title: 'Theme variables for new customer',
        author: 'mira',
        state: 'changes',
        checks: 'passing',
        additions: 88,
        deletions: 21,
        linearId: 'RUC-600',
        openedAt: hoursAgo(28),
    },
    {
        repo: 'bygr-frontend-web',
        number: 979,
        title: 'Chat: show unread count on project cards',
        author: 'jonathan',
        state: 'approved',
        checks: 'passing',
        additions: 74,
        deletions: 12,
        linearId: 'RUC-593',
        openedAt: hoursAgo(20),
    },
    {
        repo: 'bygr-database',
        number: 131,
        title: 'Add Survey.ResponseDeadline',
        author: 'sam',
        state: 'review',
        checks: 'passing',
        additions: 18,
        deletions: 0,
        linearId: 'RUC-591',
        openedAt: hoursAgo(3),
    },
    {
        repo: 'rubus-ui',
        number: 150,
        title: 'DataTable: sticky header option',
        author: 'alex',
        state: 'review',
        checks: 'failing',
        additions: 156,
        deletions: 34,
        openedAt: hoursAgo(75),
    },
];

const dummyStates: Record<string, [LinearIssue['stateType'], number]> = {
    'In progress': ['started', 1],
    'In review': ['started', 2],
    'Ready to test': ['started', 3],
    Done: ['completed', 4],
};

export const linearIssues: LinearIssue[] = (<Omit<LinearIssue, 'stateType' | 'statePosition'>[]>[
    {
        id: 'RUC-601',
        title: 'System health dashboard in MacGyver',
        state: 'In review',
        assignee: 'jonathan',
        updatedAt: minutesAgo(10),
    },
    {
        id: 'RUC-598',
        title: 'Unit search times out on large projects',
        state: 'Ready to test',
        assignee: 'sam',
        updatedAt: hoursAgo(2),
    },
    {
        id: 'RUC-595',
        title: "Project overview crashes: reading 'units'",
        state: 'Ready to test',
        assignee: 'mira',
        updatedAt: hoursAgo(3),
    },
    {
        id: 'RUC-599',
        title: 'AM/QM backend should survive core-api restarts',
        state: 'Ready to test',
        assignee: 'alex',
        updatedAt: hoursAgo(9),
    },
    {
        id: 'RUC-602',
        title: 'GitHub activity tab with real data',
        state: 'In progress',
        assignee: 'jonathan',
        updatedAt: minutesAgo(5),
    },
    {
        id: 'RUC-597',
        title: 'Communicore bounce handling for SMS',
        state: 'In progress',
        assignee: 'alex',
        updatedAt: hoursAgo(1),
    },
    {
        id: 'RUC-600',
        title: 'Flatfinder: theme variables for new customer',
        state: 'In progress',
        assignee: 'mira',
        updatedAt: hoursAgo(4),
    },
    {
        id: 'RUC-594',
        title: 'Change request summary null reference',
        state: 'Done',
        assignee: 'mira',
        updatedAt: minutesAgo(48),
    },
    { id: 'RUC-588', title: 'E2E coverage for buyer invitations', state: 'Done', assignee: 'sam', updatedAt: hoursAgo(7) },
    {
        id: 'RUC-527',
        title: 'Global user anchor lookup across profile tree',
        state: 'Done',
        assignee: 'mira',
        updatedAt: hoursAgo(20),
    },
]).map((i) => ({ ...i, stateType: dummyStates[i.state][0], statePosition: dummyStates[i.state][1] }));
